import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import camelCase from 'lodash/camelCase';
import GlobalContext from 'hooks/contexts/GlobalContext';
import cx from 'utils/classnames';
import AccordionContext from './AccordionContext';

function AccordionPanel({
  title = '',
  children,
  index,
  onActive,
  onTransitionStart,
  onTransitionEnd,
  id,
  headerComponent,
}) {
  const { activeIndex, setActiveIndex } = useContext(AccordionContext);
  const {
    windowSize: { currentBreakpoint = 'mobile' },
  } = useContext(GlobalContext);
  const isMobile = currentBreakpoint === 'mobile';
  const [height, setHeight] = useState(0);

  const accordionContentRef = useRef(null);
  const panelRef = useRef(null);
  const onTransitionStartRef = useRef(onTransitionStart);
  const onTransitionEndRef = useRef(onTransitionEnd);
  const isActive = activeIndex === index;
  // A panel that mounts already open sets its height immediately, which
  // starts a transition. Skip that one so callbacks only cover user toggles.
  const skipInitialTransitionRef = useRef(isActive);
  const accordionPanelClass = cx('accordion__panel', {
    'accordion__panel--isActive': isActive,
    [`accordion__panel--${camelCase(title)}`]: !!title,
  });

  onTransitionStartRef.current = onTransitionStart;
  onTransitionEndRef.current = onTransitionEnd;

  const handleToggle = (e) => {
    e.preventDefault();
    const nextIndex = activeIndex !== index ? index : null;

    setActiveIndex(nextIndex);
  };

  useEffect(() => {
    const body = accordionContentRef.current;
    if (!body) {
      return undefined;
    }

    const isHeightTransition = (event) =>
      event.target === body && event.propertyName === 'height';

    const handleTransitionStart = (event) => {
      if (!isHeightTransition(event) || skipInitialTransitionRef.current) {
        return;
      }

      if (onTransitionStartRef.current) {
        onTransitionStartRef.current();
      }
    };

    const handleTransitionEnd = (event) => {
      if (!isHeightTransition(event)) {
        return;
      }

      if (skipInitialTransitionRef.current) {
        skipInitialTransitionRef.current = false;
        return;
      }

      if (onTransitionEndRef.current) {
        onTransitionEndRef.current();
      }

      const panel = panelRef.current;

      if (event.type === 'transitionend' && isMobile && isActive && panel) {
        panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    };

    body.addEventListener('transitionstart', handleTransitionStart);
    body.addEventListener('transitionend', handleTransitionEnd);
    body.addEventListener('transitioncancel', handleTransitionEnd);

    return () => {
      body.removeEventListener('transitionstart', handleTransitionStart);
      body.removeEventListener('transitionend', handleTransitionEnd);
      body.removeEventListener('transitioncancel', handleTransitionEnd);
    };
  }, [isActive, isMobile]);

  useEffect(() => {
    if (isActive) {
      onActive && onActive();
      const nextHeight = accordionContentRef.current.scrollHeight;

      if (skipInitialTransitionRef.current && nextHeight === height) {
        skipInitialTransitionRef.current = false;
      }

      setHeight(nextHeight);
    }

    if (!isActive && height > 0) {
      setHeight(0);
    }
  }, [activeIndex, accordionContentRef]);

  return (
    <div className={accordionPanelClass} ref={panelRef}>
      <button
        className="accordion__header"
        type="button"
        onClick={handleToggle}
        aria-controls={`panel_${id}_${index}`}
        aria-expanded={isActive}
      >
        <span className="accordion__headerTitle">
          {headerComponent || title}
        </span>
        <span className="accordion__toggle">
          <span className="accordion__toggleLabel">Open</span>
        </span>
      </button>
      <div
        id={`panel_${id}_${index}`}
        className="accordion__body"
        ref={accordionContentRef}
        style={{ height }}
        aria-hidden={!isActive}
      >
        <div className="accordion__content">{children}</div>
      </div>
    </div>
  );
}

AccordionPanel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  index: PropTypes.number,
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  onActive: PropTypes.func,
  onTransitionStart: PropTypes.func,
  onTransitionEnd: PropTypes.func,
  headerComponent: PropTypes.node,
};

export default AccordionPanel;
